import React from 'react';
import { Sun, Youtube, Instagram, Linkedin, Mail, Phone, MapPin } from 'lucide-react';
import { mockData } from '../mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="mb-6">
              <img 
                src="/varun-solar-logo.png" 
                alt="Varun Solar Energy" 
                className="h-16 w-auto object-contain"
              />
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              {mockData.company.tagline}
            </p>
            <div className="flex space-x-4">
              <a
                href={mockData.company.socialMedia.youtube}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-red-600 transition-all duration-300 hover:scale-110"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a
                href={mockData.company.socialMedia.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className={`w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center ${mockData.company.socialMedia.facebook === '#' ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'} transition-all duration-300 hover:scale-110`}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a
                href={mockData.company.socialMedia.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className={`w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center ${mockData.company.socialMedia.instagram === '#' ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-600'} transition-all duration-300 hover:scale-110`}
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={mockData.company.socialMedia.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className={`w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center ${mockData.company.socialMedia.linkedin === '#' ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'} transition-all duration-300 hover:scale-110`}
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Company</h3>
            <ul className="space-y-3">
              <li>
                <a href="#about" className="text-gray-400 hover:text-green-500 transition-colors duration-200">
                  About Us
                </a>
              </li>
              <li>
                <a href="#services" className="text-gray-400 hover:text-green-500 transition-colors duration-200">
                  Our Services
                </a>
              </li>
              <li>
                <a href="#contact" className="text-gray-400 hover:text-green-500 transition-colors duration-200">
                  Contact Us
                </a>
              </li>
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Services</h3>
            <ul className="space-y-3">
              <li>
                <a href="#services" className="text-gray-400 hover:text-green-500 transition-colors duration-200">
                  Rooftop Solar
                </a>
              </li>
              <li>
                <a href="#services" className="text-gray-400 hover:text-green-500 transition-colors duration-200">
                  Solar Atta Chakki
                </a>
              </li>
              <li>
                <a href="#services" className="text-gray-400 hover:text-green-500 transition-colors duration-200">
                  Residential
                </a>
              </li>
              <li>
                <a href="#services" className="text-gray-400 hover:text-green-500 transition-colors duration-200">
                  Commercial
                </a>
              </li>
              <li>
                <a href="#services" className="text-gray-400 hover:text-green-500 transition-colors duration-200">
                  Maintenance
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Contact Info</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div className="flex flex-col space-y-1">
                  <a
                    href={`tel:${mockData.company.phone}`}
                    className="text-gray-400 hover:text-green-500 transition-colors duration-200 text-sm"
                  >
                    {mockData.company.phone}
                  </a>
                  <a
                    href={`tel:${mockData.company.phone2}`}
                    className="text-gray-400 hover:text-green-500 transition-colors duration-200 text-sm"
                  >
                    {mockData.company.phone2}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <a
                  href={`mailto:${mockData.company.email}`}
                  className="text-gray-400 hover:text-green-500 transition-colors duration-200 text-sm"
                >
                  {mockData.company.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">{mockData.company.address}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} {mockData.company.name}. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-green-500 transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-400 hover:text-green-500 transition-colors duration-200">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
